import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { type HaulRoute } from 'api/haul-route.api';
import { formatRoadName } from './roadsImporter.helpers';

interface DeleteRouteDialogProps {
  target: HaulRoute | null;
  pairedRoute: HaulRoute | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: (routeIds: string[]) => void;
}

export function DeleteRouteDialog({
  target,
  pairedRoute,
  deleting,
  onClose,
  onConfirm,
}: DeleteRouteDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!target) return;
    setSelectedIds(
      [target.routeId, pairedRoute?.routeId].filter(
        (id): id is string => id != null
      )
    );
  }, [target, pairedRoute]);

  const toggleId = (routeId: string) => {
    setSelectedIds((prev) =>
      prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId]
    );
  };

  return (
    <Dialog
      open={target !== null}
      onClose={() => {
        if (!deleting) onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_delete_route_title')}
      </DialogTitle>
      <DialogContent>
        {pairedRoute ? (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('roads_importer_delete_route_two_way_note')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedIds.includes(target?.routeId ?? '')}
                  onChange={() => {
                    toggleId(target?.routeId ?? '');
                  }}
                  disabled={deleting}
                />
              }
              label={
                target ? formatRoadName(target.roadName ?? target.routeKey) : ''
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedIds.includes(pairedRoute.routeId)}
                  onChange={() => {
                    toggleId(pairedRoute.routeId);
                  }}
                  disabled={deleting}
                />
              }
              label={formatRoadName(
                pairedRoute.roadName ?? pairedRoute.routeKey
              )}
            />
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('roads_importer_delete_route_confirm', {
              name: target
                ? formatRoadName(target.roadName ?? target.routeKey)
                : '',
            })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={deleting}
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={
            deleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon />
          }
          disabled={deleting || selectedIds.length === 0}
          onClick={() => {
            onConfirm(selectedIds);
          }}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
